import styled from '@emotion/styled';
import { TextField } from '@material-ui/core';
import { Send } from 'lucide-react';
import React, { useCallback, useState } from 'react';

const Footer = styled('div')`
  display: flex;
  flex-direction: column;
  background: #fff;
  border-top: 1px solid #ebedef;
  padding: 10px 12px 8px;
`;

const InputRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// "Dado Assistant is AI. It can make mistakes, so double check it." —
// subtle disclaimer under the input.
const Disclaimer = styled('div')`
  margin-top: 6px;
  text-align: center;
  font-size: 11px;
  font-weight: 300;
  color: #b0b8c1;
`;

const InputWrap = styled('div')`
  flex: 1;
  min-height: 36px;
  border-radius: 6px;
  box-shadow: inset 0 0 0 1px #dde0e3;
  padding: 0 12px;
  display: flex;
  align-items: center;
  transition: box-shadow 120ms ease;

  &:focus-within {
    box-shadow:
      inset 0 0 0 1px #0e78e4,
      0 0 0 3px rgba(14, 120, 228, 0.18);
  }
`;

const SendButton = styled('button')<{ disabled?: boolean }>`
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 6px;
  background: ${(p) => p.theme.dado.copilot.gradientDiagonal};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  color: #fff;
  box-shadow: ${(p) => p.theme.dado.copilot.sendButtonShadow};
  transition: filter 120ms ease, opacity 120ms ease;

  &:hover:not(:disabled) {
    filter: brightness(1.05);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  &:focus-visible {
    outline: none;
    box-shadow:
      ${(p) => p.theme.dado.copilot.sendButtonShadow},
      0 0 0 3px rgba(14, 120, 228, 0.25);
  }
`;

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled?: boolean;
}

export const ChatInput: React.FC<React.PropsWithChildren<ChatInputProps>> = ({
  onSend,
  disabled,
}) => {
  const [value, setValue] = useState('');

  const handleSend = useCallback(() => {
    const trimmed = value.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setValue('');
  }, [value, disabled, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  return (
    <Footer>
      <InputRow>
        <InputWrap>
          <TextField
            fullWidth
            // Force standard variant: the app theme defaults TextField to
            // `outlined`, whose notched <fieldset> border rendered INSIDE
            // the InputWrap border (rectangle-in-a-rectangle). Standard +
            // disableUnderline = borderless; InputWrap is the only border.
            variant="standard"
            placeholder="Ask Assistant..."
            multiline
            maxRows={3}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            InputProps={{
              disableUnderline: true,
              style: { fontSize: 13, fontWeight: 300, lineHeight: '1.5' },
            }}
          />
        </InputWrap>
        <SendButton
          type="button"
          onClick={handleSend}
          disabled={disabled || !value.trim()}
          aria-label="Send message"
        >
          <Send size={14} />
        </SendButton>
      </InputRow>
      <Disclaimer>Dado Assistant is AI. It can make mistakes, so double check it.</Disclaimer>
    </Footer>
  );
};
